import { z } from "zod";
import { Timestamps } from "../common/timestamps.schema.ts";
import { CustomerId } from "../fsm/fsm.schema.ts";
import { BikeId } from "../bikes/bikes.schema.ts";

// core_devices / core_device_state (BEL-581, ADR 042 §1, ADR 043). Values here
// intentionally mirror the public /v1 Partner API's provider-neutral vocabulary
// (api/src/modules/partner/partner.enums.ts) — NOT Linka's native vocabulary
// (ADR 043 Rejected Alternatives (a)).
export const DeviceId = z.string().uuid().brand<"DeviceId">();
export type DeviceId = z.infer<typeof DeviceId>;

export const DeviceProvider = z.enum(["linka"]);
export type DeviceProvider = z.infer<typeof DeviceProvider>;

export const DeviceType = z.enum(["lock"]);
export type DeviceType = z.infer<typeof DeviceType>;

// Registry lifecycle of the device record (not the live lock state).
export const DeviceStatus = z.enum(["active", "decommissioned"]);
export type DeviceStatus = z.infer<typeof DeviceStatus>;

// Linka-specific hardware variant. Informational only — never exposed to /v1
// (ADR 043 §8, "provider is never leaked").
export const LockType = z.enum(["leo2", "lasso"]);
export type LockType = z.infer<typeof LockType>;

// Same 10 values as DeviceCapability in partner.enums.ts.
export const DeviceCapability = z.enum([
  "remote_lock",
  "remote_unlock",
  "alarm",
  "gps",
  "app_reported_location",
  "tamper_detection",
  "battery_percent",
  "battery_voltage",
  "ebike_telemetry",
  "lock_state_transitional",
]);
export type DeviceCapability = z.infer<typeof DeviceCapability>;

// Declared, verifiable fleet membership (BEL-682) — the code guard that
// blocks LINKA commands from reaching MB92 reads this field, never which
// LINKA credential happens to be loaded in the environment. Nullable only
// until the backfill migration runs.
export const DeviceFleet = z.enum(["mb92", "belora_mobility"]);
export type DeviceFleet = z.infer<typeof DeviceFleet>;

export const DeviceDto = z
  .object({
    id: DeviceId,
    customerId: CustomerId,
    provider: DeviceProvider,
    deviceType: DeviceType,
    providerDeviceId: z.string(),
    lockType: LockType.nullable(),
    capabilities: z.array(DeviceCapability),
    status: DeviceStatus,
    // NOT NULL in `core_devices` (BEL-682) — every persisted device row
    // always has a declared fleet by the time this DTO is read.
    fleet: DeviceFleet,
    bikeId: BikeId.nullable(),
    isActive: z.boolean().default(true),
    deactivatedAt: z.string().nullable().optional(),
    deactivatedBy: z.string().uuid().nullable().optional(),
  })
  .merge(Timestamps);
export type DeviceDto = z.infer<typeof DeviceDto>;

// 6 neutral values (ADR 043 §3): locked/unlocked/unknown are universal;
// locking/unlocking/jammed only appear on devices declaring the
// lock_state_transitional capability.
export const LockState = z.enum([
  "locked",
  "unlocked",
  "unknown",
  "locking",
  "unlocking",
  "jammed",
]);
export type LockState = z.infer<typeof LockState>;

// `unknown` (BEL-627): a BLE-only device has no persistent link, so
// online/offline does not apply — see partner.enums.ts's Connectivity for the
// full rationale this mirrors.
export const Connectivity = z.enum(["online", "offline", "unknown"]);
export type Connectivity = z.infer<typeof Connectivity>;

// Class-level connectivity (BEL-627) — derived from capabilities, not stored:
// a device that can accept remote_lock/remote_unlock/alarm needs a persistent
// (cellular) link; one that cannot is short_range (e.g. BLE-only).
export const ConnectivityMode = z.enum(["cellular", "short_range"]);
export type ConnectivityMode = z.infer<typeof ConnectivityMode>;

export const DeviceStateDto = z.object({
  deviceId: DeviceId,
  customerId: CustomerId,
  lockState: LockState,
  connectivity: Connectivity,
  batteryPercent: z.number().int().min(0).max(100).nullable(),
  batteryVoltage: z.number().nullable(),
  ebikeBatteryPercent: z.number().int().min(0).max(100).nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  locationAccuracyM: z.number().int().nullable(),
  // Independent, co-occurring flags (ADR 043 §5) — NOT a mutually exclusive enum.
  securityTamper: z.boolean(),
  securityMovement: z.boolean(),
  securityImpact: z.boolean(),
  firmwareVersion: z.string().nullable(),
  // Raw LINKA trigger data (BEL-586 requisito 6) — never classified/exposed
  // here, that is BEL-608's job. `null` is the normal case (LEO 2 never
  // sends it; LEO 2 Pro only on the ping following our own command).
  lastStateReason: z.string().nullable(),
  // Raw `station_can_id`, persisted alongside the trigger — never resolved
  // against `core_stations.id` (BEL-586, decided 2026-08-10).
  lastStationCanId: z.string().nullable(),
  lastSeenAt: z.string().nullable(),
  // BEL-587: last time latitude/longitude actually changed — NOT the same as
  // `updatedAt`, which advances on every telemetry poll regardless of whether
  // the position moved. Drives the map view's position-age display.
  lastLocationAt: z.string().nullable(),
  updatedAt: z.string(),
});
export type DeviceStateDto = z.infer<typeof DeviceStateDto>;

// BEL-583 request contracts — API layer only, reusing BEL-581's DTOs/enums.
// `capabilities` is explicitly declared at registration time in Phase 1 (no
// Linka discovery yet — ticket's "Alcance (No incluye)").
export const RegisterDeviceInput = z.object({
  // Explicit — a device is directly customer-scoped and may be registered
  // unattached (inventory, no bikeId to derive it from).
  customerId: CustomerId,
  provider: DeviceProvider,
  deviceType: DeviceType,
  providerDeviceId: z.string().min(1),
  // Required, never defaulted, never derived (BEL-729). The BEL-682 guard
  // decides whether a command may reach a device by reading this field, so a
  // wrong value here is what puts a customer-site lock within reach of a
  // remote unlock. It is independent of `customerId`: fleet says which LINKA
  // account the lock answers to, customerId says who operates it. A lock
  // bought for MB92 and parked in inventory under Belora's own customer is
  // still `mb92`.
  fleet: DeviceFleet,
  lockType: LockType.nullable().optional(),
  capabilities: z.array(DeviceCapability).default([]),
  bikeId: BikeId.nullable().optional(),
});
export type RegisterDeviceInput = z.infer<typeof RegisterDeviceInput>;

export const AssociateDeviceInput = z.object({
  bikeId: BikeId,
});
export type AssociateDeviceInput = z.infer<typeof AssociateDeviceInput>;

// Manual state write (Phase 1 — ADR 042 §2). Every field optional (partial
// patch); only the ADR 043 neutral vocabulary is accepted — Linka's native
// spellings (Locking/Unlocking/Locked/Unlocked/Stalled) are rejected by
// `LockState`'s enum values alone, before any capability check runs.
export const SetDeviceStateInput = z.object({
  lockState: LockState.optional(),
  connectivity: Connectivity.optional(),
  batteryPercent: z.number().int().min(0).max(100).nullable().optional(),
  batteryVoltage: z.number().nullable().optional(),
  ebikeBatteryPercent: z.number().int().min(0).max(100).nullable().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  locationAccuracyM: z.number().int().nullable().optional(),
  securityTamper: z.boolean().optional(),
  securityMovement: z.boolean().optional(),
  securityImpact: z.boolean().optional(),
  firmwareVersion: z.string().nullable().optional(),
});
export type SetDeviceStateInput = z.infer<typeof SetDeviceStateInput>;
